import React, { useEffect, useState } from "react";
import { Box, Button, Collapse, styled } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSearchParams } from "react-router-dom";
import {
  HOME_HIGHLIGHTS_COLLAPSE,
  HOME_HIGHLIGHTS_EXPAND,
  HOME_HIGHLIGHTS_LABEL,
  SEARCH_QUERY_PARAM,
} from "../../pages/shared/constants";

const CONTENT_ID = "promociones-y-servicios";

const ToggleRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.common.white,
}));

const ToggleButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  color: theme.palette.text.primary,
  fontSize: theme.typography.pxToRem(13),
  fontWeight: 500,
  width: "100%",
  paddingTop: theme.spacing(0.75),
  paddingBottom: theme.spacing(0.75),
  borderRadius: 0,
}));

const Chevron = styled(ExpandMoreIcon, { shouldForwardProp: (prop) => prop !== "open" })<{
  open: boolean;
}>(({ open }) => ({
  transition: "transform 0.2s ease-in-out",
  transform: open ? "rotate(180deg)" : "none",
  fontSize: 20,
}));

/**
 * The banner and the services row, collapsed while a search is running.
 *
 * Together they fill the entire first screen, so a shopper who searched saw no products at all and
 * could not tell whether anything matched. Collapsing gets results above the fold instead of asking
 * them to scroll to find out.
 *
 * The toggle sits *below* the content rather than above it: that keeps it in the same place on the
 * page whether the block is open or shut, and keeps a control bar from sitting on top of the hero
 * banner.
 */
const HomeHighlights: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const isSearching = Boolean(searchParams.get(SEARCH_QUERY_PARAM)?.trim());

  const [open, setOpen] = useState(!isSearching);

  // Tracks whether a search is running at all, not the term itself — so this fires when a search
  // starts or ends, and not on every keystroke. That is what lets someone reopen the block
  // mid-search and have it stay open while they keep typing.
  useEffect(() => {
    setOpen(!isSearching);
  }, [isSearching]);

  return (
    <Box>
      {/* `unmountOnExit` so a hidden banner stops running its autoplay timer and stops decoding
          images nobody is looking at. RTK Query has the banners cached, so reopening costs no
          request. */}
      <Collapse in={open} unmountOnExit>
        <Box id={CONTENT_ID}>{children}</Box>
      </Collapse>

      <ToggleRow>
        <ToggleButton
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-controls={CONTENT_ID}
          aria-label={open ? HOME_HIGHLIGHTS_COLLAPSE : HOME_HIGHLIGHTS_EXPAND}
          endIcon={<Chevron open={open} />}
        >
          {HOME_HIGHLIGHTS_LABEL}
        </ToggleButton>
      </ToggleRow>
    </Box>
  );
};

export default HomeHighlights;
